import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import auth from "../../fire";
import { signOut } from "firebase/auth";
import axiosClient from "../../axiosClient";
import { ClipLoader } from "react-spinners";
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import AuthActionHeader from "./AuthActionHeader";

type ConfirmStatus = "confirming" | "success" | "error";

interface ConfirmEmailChangeActionProps {
  token: string | null;
}

interface ConfirmEmailChangeResponse {
  newEmail?: string;
  message?: string;
}

// Confirms an email change using our own one-time token (verify-before-change).
// The server commits the change and revokes refresh tokens, so we sign out and
// have the user log back in with the new address. Run-once ref guards against
// StrictMode's double invocation consuming the single-use token twice.
const ConfirmEmailChangeAction = ({
  token,
}: ConfirmEmailChangeActionProps) => {
  const [status, setStatus] = useState<ConfirmStatus>("confirming");
  const [newEmail, setNewEmail] = useState("");
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    const run = async () => {
      if (!token) {
        setStatus("error");
        return;
      }
      try {
        const res = await axiosClient.post<ConfirmEmailChangeResponse>(
          "/api/users/confirm-email-change",
          { token }
        );
        setNewEmail(res.data?.newEmail || "");
        // The change revoked the old session's tokens — sign out so the next
        // login uses the new email.
        try {
          await signOut(auth);
        } catch (e) {
          /* non-fatal */
        }
        setStatus("success");
      } catch (e) {
        setStatus("error");
      }
    };
    run();
  }, [token]);

  if (status === "confirming") {
    return (
      <div className="flex items-center justify-center py-24">
        <ClipLoader
          color="rgb(79 70 229)"
          speedMultiplier={0.6}
          size={"2rem"}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col py-12 px-4">
      <AuthActionHeader />
      {status === "success" ? (
        <>
          <CheckCircleIcon
            className="h-auto w-12 mx-auto text-indigo-600"
            aria-hidden="true"
          />
          <h2 className="text-center mt-6 text-3xl font-bold tracking-tight text-gray-900">
            Email updated
          </h2>
          <p className="text-center text-sm text-gray-900 mt-2">
            {newEmail
              ? `Your account email is now ${newEmail}. Please log in again.`
              : "Your account email has been updated. Please log in again."}
          </p>
        </>
      ) : (
        <>
          <ExclamationTriangleIcon
            className="h-auto w-12 mx-auto text-red-500"
            aria-hidden="true"
          />
          <h2 className="text-center mt-6 text-2xl font-bold tracking-tight text-gray-900">
            Email change link issue
          </h2>
          <p className="text-center text-sm text-gray-900 mt-2">
            This confirmation link is invalid or has expired. Please request the
            email change again from your account page.
          </p>
        </>
      )}
      <div className="px-4 py-4 w-full flex justify-center fixed bottom-0 bg-gray-50">
        <Link to="/login" className="text-sm text-indigo-600 font-medium">
          Go to login
        </Link>
      </div>
    </div>
  );
};

export default ConfirmEmailChangeAction;
